import * as React from 'react';
import { useSelector } from 'react-redux';

import { currentOpenChatSelector } from '../../selectors';
import { useIsReplyableChat } from '../../hooks/useIsReplyableChat';
import { useReplyOnboardingEnabled } from '../../hooks/useReplyOnboardingEnabled';
import { findLastIndex } from '../../utils/findLastIndex';
import { isReplyableMessage, isSendMessageAvailable } from '../../utils/replyMessageAvailability';
import { isReplySwipeOnboardingShown, markReplySwipeOnboardingShown } from '../../utils/replySwipeOnboarding';
import { ReplyOnboardingContext } from '../../utils/replyOnboardingContext';
import { useRepliesEnabled } from '../../hooks/useRepliesEnabled';

interface IReplyOnboardingProviderProps {
  /** Оставлено для вызова из ChatScrollContainer; таргет считается по всему чату */
  isLastPage?: boolean;
  children: React.ReactNode;
}

export const ReplyOnboardingProvider: React.FC<IReplyOnboardingProviderProps> = ({ children }) => {
  const { chatId = '', chatMessages = [], availableActions } = useSelector(currentOpenChatSelector) ?? {};
  const isRepliesEnabled = useRepliesEnabled();
  const isReplyOnboardingEnabled = useReplyOnboardingEnabled();
  const isReplyableChat = useIsReplyableChat(chatId);

  const [isOnboardingActive, setIsOnboardingActive] = React.useState(false);
  const prevChatIdRef = React.useRef(chatId);

  React.useEffect(() => {
    if (prevChatIdRef.current !== chatId) {
      prevChatIdRef.current = chatId;
      setIsOnboardingActive(false);
    }

    if (
      !isRepliesEnabled ||
      !isReplyOnboardingEnabled ||
      !isReplyableChat ||
      !isSendMessageAvailable(availableActions)
    ) {
      setIsOnboardingActive(false);

      return;
    }

    const hasOnboardingBeenShown = isReplySwipeOnboardingShown();

    if (!hasOnboardingBeenShown) {
      setIsOnboardingActive(true);
    }
  }, [chatId, isReplyableChat, availableActions, isRepliesEnabled, isReplyOnboardingEnabled]);

  const complete = React.useCallback(() => {
    setIsOnboardingActive(false);
  }, []);

  const onboardingTargetMessageId = React.useMemo(() => {
    const index = findLastIndex(chatMessages, ({ message }) => isReplyableMessage(message));

    return index >= 0 ? chatMessages[index].messageId : undefined;
  }, [chatMessages]);

  const isReplyOnboardingTargetMessage = React.useCallback(
    (messageId: string): boolean =>
      Boolean(isOnboardingActive && onboardingTargetMessageId && messageId === onboardingTargetMessageId),
    [isOnboardingActive, onboardingTargetMessageId],
  );

  const value = React.useMemo(
    () => ({
      isReplyOnboardingTargetMessage,
      complete,
      onOnboardingShown: markReplySwipeOnboardingShown,
    }),
    [isReplyOnboardingTargetMessage, complete],
  );

  return <ReplyOnboardingContext.Provider value={value}>{children}</ReplyOnboardingContext.Provider>;
};
